import * as CalculatorStepTracker from "@omni/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@omni/services/tracking/trackers/tools/calculators/config";
import { parseFloatSafe } from "@omni/utils/number.utils";
import queryString from "query-string";
import { PureComponent } from "react";

import { asyncState, isValidPositiveNumber } from "@/utils/calculators";

import Calculation from "./calculation/calculation.component";
import Result from "./result/result.component";

interface BrickCalculatorState {
    wallLength: string;
    wallHeight: string;
    brickWidth: string;
    brickLength: string;
    brickHeight: string;
    wallArea: number;
    brickArea: number;
    numberOfBricksHalf: number;
    numberOfBricksFull: number;
    mortarVolumeHalf: number;
    mortarVolumeFull: number;
    jointCementVolume: number;
}

class BrickCalculator extends PureComponent<{}, BrickCalculatorState> {
    query = queryString.parse(location.search);

    constructor(props: {}) {
        super(props);

        this.state = {
            wallLength: this.query.wallLength?.toString() || "0",
            wallHeight: this.query.wallHeight?.toString() || "0",
            brickWidth: this.query.brickWidth?.toString() || "0",
            brickLength: this.query.brickLength?.toString() || "0",
            brickHeight: this.query.brickHeight?.toString() || "0",
            wallArea: 0,
            brickArea: 0,
            numberOfBricksHalf: 0,
            numberOfBricksFull: 0,
            mortarVolumeHalf: 0,
            mortarVolumeFull: 0,
            jointCementVolume: 0
        };
    }

    async componentDidMount() {
        await this.calculateFields(true);
    }

    // calculations
    wallArea = () => {
        return (
            parseFloatSafe(this.state.wallLength, 0) *
            parseFloatSafe(this.state.wallHeight, 0)
        );
    };
    brickArea = () => {
        let brickArea = 0;

        const brickLength = parseFloatSafe(this.state.brickLength, 0);

        const brickHeight = parseFloatSafe(this.state.brickHeight, 0);

        if (
            isValidPositiveNumber(brickLength) &&
            isValidPositiveNumber(brickHeight)
        ) {
            brickArea = (brickLength + 1) * (brickHeight + 1) * 0.0001;
        }

        return brickArea;
    };
    numberOfBricks = (adjustmentFactor: number) => {
        let numberOfBricks = 0;

        const wallArea = parseFloatSafe(this.state.wallArea, 0);

        const brickArea = parseFloatSafe(this.state.brickArea, 0);

        if (
            isValidPositiveNumber(wallArea) &&
            isValidPositiveNumber(brickArea)
        ) {
            numberOfBricks = (wallArea / brickArea) * adjustmentFactor;
        }

        return numberOfBricks;
    };
    mortarVolumeHalf = () => {
        const brickLength = parseFloatSafe(this.state.brickLength, 0);

        const brickWidth = parseFloatSafe(this.state.brickWidth, 0);

        const brickHeight = parseFloatSafe(this.state.brickHeight, 0);

        const brickVolume =
            brickLength * brickWidth * brickHeight * 0.001 * 0.001;

        const volumeTotalBricks = brickVolume * this.state.numberOfBricksHalf;

        const wallVolume = this.state.wallArea * brickWidth * 0.01;

        return wallVolume - volumeTotalBricks;
    };
    mortarVolumeFull = () => {
        const mortarInBetweenDoubleWall = this.wallArea() * 0.01; // wall area x 1 centimeter
        const mortarVolumeHalf = this.mortarVolumeHalf();

        return mortarVolumeHalf * 2 + mortarInBetweenDoubleWall;
    };
    joinCementVolume = () => {
        const areaJointCement = 0.01 * 0.01;

        const numberOfBricks = this.state.numberOfBricksHalf;

        const brickHeight = parseFloatSafe(this.state.brickHeight, 0) / 100;

        const brickLength = parseFloatSafe(this.state.brickLength, 0) / 100;
        const brickDeadSpace = areaJointCement * 0.01;

        return (
            brickLength * numberOfBricks * areaJointCement +
            brickHeight * numberOfBricks * areaJointCement +
            brickDeadSpace * numberOfBricks
        );
    };

    // setters
    setBrickMeasurements = (length: string, width: string, height: string) => {
        this.setState(
            {
                brickLength: length,
                brickWidth: width,
                brickHeight: height
            },
            () => {
                this.calculateFields();
            }
        );
    };
    setCalculationValue = (field: string, value: string) => {
        this.setState(
            (state) => ({ ...state, [field]: value }),
            async () => {
                await this.calculateFields();
            }
        );
    };
    calculateFields = async (initialCall: boolean = false) => {
        await asyncState(this, {
            wallArea: this.wallArea(),
            brickArea: this.brickArea()
        });
        await asyncState(this, {
            numberOfBricksHalf: Math.ceil(this.numberOfBricks(1.02)),
            numberOfBricksFull: Math.ceil(this.numberOfBricks(2.04))
        });
        await asyncState(this, {
            mortarVolumeHalf: this.mortarVolumeHalf(),
            mortarVolumeFull: this.mortarVolumeFull(),
            jointCementVolume: this.joinCementVolume()
        });

        if (initialCall) {
            CalculatorStepTracker.trackStart(CALCULATOR_KEYS.brick);
        } else {
            CalculatorStepTracker.trackSubmit(CALCULATOR_KEYS.brick);
        }
    };

    render() {
        const calculationProps = {
            setCalculationValue: this.setCalculationValue,
            setBrickMeasurements: this.setBrickMeasurements,
            wallLength: this.state.wallLength,
            wallHeight: this.state.wallHeight,
            brickLength: this.state.brickLength,
            brickWidth: this.state.brickWidth,
            brickHeight: this.state.brickHeight
        };
        const resultsProps = {
            brickWidth: this.state.brickWidth,
            brickLength: this.state.brickLength,
            wallArea: this.state.wallArea,
            numberOfBricksHalf: this.state.numberOfBricksHalf,
            numberOfBricksFull: this.state.numberOfBricksFull,
            mortarVolumeHalf: this.state.mortarVolumeHalf,
            mortarVolumeFull: this.state.mortarVolumeFull,
            jointCementVolume: this.state.jointCementVolume,
            mailParams: {
                wallLength: this.state.wallLength,
                wallHeight: this.state.wallHeight,
                brickLength: this.state.brickLength,
                brickWidth: this.state.brickWidth,
                brickHeight: this.state.brickHeight
            }
        };

        return (
            <div id="calculator">
                <Calculation {...calculationProps} />
                <Result {...resultsProps} />
            </div>
        );
    }
}

export { BrickCalculator };
